<template>
  <Head :title="account.email ? $t('update :t', { t: account.name }) : $t('create account')" />

  <main class="accounts update">
    <section>
      <header>
        <div>
          <h2>{{ $t('Accounts') }}</h2>
          <h1>{{ account.email ? account.name : $t('Create account') }}</h1>
        </div>
      </header>

      <form @submit.prevent="submitForm">
        <fieldset>
          <div class="field">
            <label>{{ $t('name') }}</label>
            <div>
              <input v-model="form.name" type="text" name="name" />
              <FormError :error="form.errors.name" />
            </div>
          </div>

          <div class="field">
            <label>naam partner</label>
            <div>
              <input v-model="form.partner" type="text" name="partner" />
              <FormError :error="form.errors.partner" />
            </div>
          </div>

          <div class="field">
            <label>foto</label>
            <div>
              <FileInput v-model="form.images" :multiple="false" accept="image/*" />
              <FormError :error="form.errors.images" />
            </div>
          </div>
        </fieldset>

        <fieldset>
          <div class="field">
            <label>{{ $t('email') }}</label>
            <div>
              <input v-model="form.email" type="email" name="email" />
              <FormError :error="form.errors.email" />
            </div>
          </div>

          <div class="field">
            <label>telefoon</label>
            <div>
              <input v-model="form.phone" type="tel" name="phone" />
              <FormError :error="form.errors.phone" />
            </div>
          </div>
        </fieldset>

        <fieldset>
          <div class="field">
            <label>adres</label>
            <div>
              <div>
                <input v-model="form.addressAddressLine" type="text" name="address-line" />
                <FormError :error="form.errors.addressAddressLine" />
              </div>
            </div>
          </div>

          <div class="field-group">
            <div class="field">
              <label>postcode</label>
              <div>
                <input v-model="form.addressPostalcode" type="text" name="postal-code" />
                <FormError :error="form.errors.addressPostalcode" />
              </div>
            </div>

            <div class="field">
              <label>plaats</label>
              <div>
                <input v-model="form.addressLocality" type="text" name="locality" />
                <FormError :error="form.errors.addressLocality" />
              </div>
            </div>
          </div>

          <div class="field">
            <label>land</label>
            <div>
              <select v-model="form.addressCountryCode">
                <option value="NL">Nederland</option>
                <option value="BE">België</option>
              </select>
              <FormError :error="form.errors.addressCountryCode" />
            </div>
          </div>
        </fieldset>

        <fieldset>
          <div class="field">
            <label>{{ $t('roles') }}</label>
            <div>
              <label v-for="(role, index) in roles" :key="index" class="checkbox role">
                <input v-model="form.roles" type="checkbox" name="roles" :value="role.name" />
                <div class="label">
                  {{ role.title }}
                  <template v-if="role.description">
                    <div class="help prose pre">{{ role.description }}</div>
                  </template>
                </div>
              </label>

              <FormError :error="form.errors.roles" />
            </div>
          </div>
        </fieldset>

        <footer>
          <fieldset class="actions">
            <button type="submit" class="success" :disabled="form.processing">
              <Icon name="plus" /> {{ $t('save') }}
            </button>
          </fieldset>
        </footer>
      </form>
    </section>
  </main>
</template>

<script lang="ts" setup>
import { Head, useForm } from '@inertiajs/vue3'
import { toast } from 'vue-sonner'

import FileInput from '/@admin:components/FileInput.vue'
import FormError from '/@admin:components/FormError.vue'
import Icon from '/@admin:components/Icon.vue'
import AuthLayout from '/@admin:layouts/Auth.vue'
import { $t } from '/@admin:shared/i18n'
import type { User, UserRole } from '/@admin:types'

defineOptions({
  layout: [AuthLayout],
})

const props = defineProps<{
  account: User
  roles: UserRole[]
  details?: { name: string }
}>()

const form = useForm({
  name: props.account.name,
  email: props.account.email,
  phone: props.account.profile?.phone || '',
  images: props.account.images,
  partner: props.account.profile?.partner || '',
  addressAddressLine: props.account.profile?.address?.addressLine || '',
  addressPostalcode: props.account.profile?.address?.postalCode || '',
  addressLocality: props.account.profile?.address?.locality || '',
  addressCountryCode: props.account.profile?.address?.countryCode || 'NL',
  roles: (props.account.roles ?? []).map((r) => r.name),
})

const submitForm = () => {
  const exists = !!props.account.sqid

  form.post(location.pathname, {
    onSuccess: () => {
      if (!exists) {
        toast.success($t('Account added.'))
      } else {
        toast.success($t('Account saved'))
      }
    },
  })
}
</script>

<!-- <style src="/@admin:css/views/accounts.update.css"></style> -->
